'use strict';

/* global _satellite */

const baseQuickViewJS = require('brand_core/product/quickView');
const breakpoints = require('brand_core/components/breakpoints');
const utils = require('../components/utils');
const cartutils = require('../components/cartutils');
const throttle = require('lodash/throttle');
const promoCode = require('../order/promoCode');
const basketHelper = require('../helper/basketHelper');

let quantityChangeDebounce;
let $idMeDrawer;

/**
 * renders an error alert at the top of the page.
 * @param {Object} message - Error message to display
 */
function createErrorNotification(message) {
    $('.cart-error').empty().alert({
        content: message,
        theme: 'danger',
        placement: 'inline',
        autoClose: false
    });
}

/**
 * Dynamically creates modal. Used by `.js-cart-modal` on `.share-my-cart .btn`
 * @param {Object} $this - element from create login modal event
 */
function createModal($this) {
    const $existingModal = $(`#${$this.data('id')}`);
    if (!$existingModal.length) {
        $.get($this.data('url')).then((data) => {
            const $modal = $.modal({
                title: $this.data('title'),
                content: data,
                modalSizeClass: 'modal-md',
                modalId: $this.data('id'),
                customClasses: $this.data('customClasses')
            });

            $modal.on('shown.bs.modal', () => {
                const $modalForm = $modal.find('form');
                const $recaptchaBtn = $modalForm.find('.btn-recaptcha');
                /* global registerButtonAsRecaptchaTrigger */
                registerButtonAsRecaptchaTrigger($recaptchaBtn);
                $modalForm.trigger('form:created');
            });
        });
    } else {
        $existingModal.modal('show');
    }
}

/**
 * Updates the shipping methods select box with new/updated options.
 * @param {Array} shippingMethods array of shipping methods data
 */
function updateShippingMethodsSelect(shippingMethods) {
    if (shippingMethods && shippingMethods.length) {
        const $shippingMethodSelect = $('.shippingMethods');
        const selectedShippingMethod = $shippingMethodSelect.find(':selected').val();
        $shippingMethodSelect.empty();

        shippingMethods.forEach((shippingMethod) => {
            $shippingMethodSelect.append(`<option data-shipping-id="${shippingMethod.ID}" value="${shippingMethod.ID}" ${shippingMethod.ID === selectedShippingMethod ? 'selected' : ''}>${shippingMethod.displayName} (${shippingMethod.description}) ${shippingMethod.shippingCost}</option>`);
        });
    }
}

/**
 * Updates the availability of a product line item
 * @param {Object} lineItem - AJAX response from the server
 * @param {jQuery} $productCard - The product card in which to update the availability messaging
 */
function updateAvailability(lineItem, $productCard) {
    const $availabilityMsgContainer = $productCard.find('.product-card__availability').empty();

    if (lineItem.availability && lineItem.availability.messages) {
        lineItem.availability.messages.forEach(function (message) {
            $availabilityMsgContainer.alert({
                content: message.content,
                theme: message.theme,
                dismissible: false,
                autoClose: false
            });
        });
    }
}

/**
 * Updates the availability of a product line item
 * @param {Array} uuidArray - An array of product line item UUIDs that had their quantity auto-updated by the server
 */
function updateCartQuantityChangeMessage(uuidArray) {
    uuidArray.forEach(uuid => {
        const $productCard = $(`.product-card[data-uuid=${uuid}]`);
        const newQuantity = $productCard.find('.js-quantity').val();

        const syntheticLineItem = {
            availability: {
                messages: [{
                    content: window.resources.quantityUpdated.replace('{0}', newQuantity),
                    theme: 'danger',
                    dismissible: true
                }]
            }
        };
        updateAvailability(syntheticLineItem, $productCard);
    });
}

/**
 * Removes product card when an item has been automatically deleted by cart middleware
 * @param {Array} uuidArray - An array of product line item UUIDs deleted by the server
 */
function removeDeletedProductCards(uuidArray) {
    uuidArray.forEach(uuid => {
        const $productCard = $(`.product-card[data-uuid=${uuid}]`);
        $productCard.remove();
    });
}

/**
 * Updates the velocity messaging of a product line item ("Only 4 left, order soon")
 * @param {Object} lineItem - AJAX response from the server
 * @param {jQuery} $productCard - The product card in which to update the velocity messaging
 */
function updateVelocityMessaging(lineItem, $productCard) {
    const $velocityMessageContainer = $productCard.find('.line-item__message--velocity').empty();

    if (lineItem.velocityMessage && lineItem.velocityMessage.messages && lineItem.velocityMessage.messages.length) {
        lineItem.velocityMessage.messages.forEach(message => {
            $velocityMessageContainer.append(`
                <div class="d-flex align-items-center text-danger">
                    <svg class="svgicon mr-1"><use href="#time"></use></svg>
                    ${message.text}
                </div>
            `);
        });
    }
}

/**
 * Updates product's quantity drop-down options on cart page
 * @param {Object} lineItem - AJAX response from the server
 */
function updateProductQuantity(lineItem) {
    if (lineItem && lineItem.quantityOptions && lineItem.quantity) {
        const minQuantity = lineItem.quantityOptions.minOrderQuantity;
        const maxQuantity = lineItem.quantityOptions.maxOrderQuantity;

        if (minQuantity && maxQuantity && minQuantity <= maxQuantity) {
            const $qtyField = $(`.product-card[data-uuid=${lineItem.UUID}]`).find('.js-quantity');
            const qtyValue = Math.min(Math.max(lineItem.quantity, minQuantity), maxQuantity); // make sure qty is between min and max

            $qtyField.attr({ min: minQuantity, max: maxQuantity });
            $qtyField.val(qtyValue);

            if (!lineItem.isGiftCard) {
                $qtyField.closest('.input-group').find('.js-quantity-add')
                    .prop('disabled', (qtyValue >= maxQuantity)); // toggle disabled state of + button
            }
        }
    }
}

/**
 * Updates all the product cards on the cart page
 * @param {Object} items Response data from server
 */
function updateAllProductCards(items) {
    items.forEach(function (lineItem) {
        const $productCard = $(`.product-card[data-uuid=${lineItem.UUID}]`);

        if (!lineItem.isBonusProductLineItem) {
            $productCard.find('.line-item-promo').html(lineItem.renderedPromotions);
        }

        $productCard.find(`.item-total-${lineItem.UUID}`).html(lineItem.priceTotal.renderedPrice);

        if (!lineItem.isGiftCard) {
            $productCard.find('.product-card__price .price').html(lineItem.priceItem.renderedPrice);
        }

        updateAvailability(lineItem, $productCard);
        updateVelocityMessaging(lineItem, $productCard);
        updateProductQuantity(lineItem);

        // Forecasted Loyalty
        const itemWillEarnRewards = Boolean(lineItem.rewards && lineItem.rewards.promoRewards.value > 0);
        $productCard.find('.js-cart-rewards-message').toggleClass('d-none', !itemWillEarnRewards);
        $productCard.find('.js-cart-rewards-message-zero').toggleClass('d-none', itemWillEarnRewards);

        if (itemWillEarnRewards) {
            $productCard.find('.js-cart-loyalty-actual').text(lineItem.rewards.promoRewards.formatted);
            $productCard.find('.js-cart-loyalty-strike').html(lineItem.rewards.strikeThrough); // This value contains HTML tags, hence html() vs text()
        }
    });
}

/**
 * re-renders the order totals and the number of items in the cart
 * @param {Object} data - AJAX response from the server
 */
function updateCartTotals(data) {
    $('.js-number-items').text(data.resources.numberOfItems);

    // broadcast events
    $(document).trigger('addRemoveCoupon:updateShippingMethod', data);
    $(document).trigger('minicart:updatecount', data.numItems);
    window.dispatchEvent(new Event('cart:updated:dtm'));

    const $summaryContainer = $('.order-totals-wrapper');
    const freeShippingText = window.resources && window.resources.localizedFreeShippingText;
    const isAdjustedShippingCostFree = freeShippingText && data.totals.adjustedShippingTotalCost.toLowerCase() === freeShippingText.toLowerCase();

    // Subtotal, shipping, tax, grand total
    $('.js-subtotal-value').text(data.totals.subTotal);
    $summaryContainer.find('.shipping-total-cost').text(isAdjustedShippingCostFree ? data.totals.adjustedShippingTotalCost : data.totals.totalShippingCost);
    $summaryContainer.find('.tax-total').text(data.totals.totalTax);
    $summaryContainer.find('.grand-total-sum').text(data.totals.grandTotal);

    // Order-level discount
    const $orderDiscountRow = $summaryContainer.find('.order-discount');
    $orderDiscountRow.toggleClass('hide-order-discount', !data.totals.orderLevelDiscountTotal.value);
    $orderDiscountRow.find('.value').text(`- ${data.totals.orderLevelDiscountTotal.formatted}`);

    // "You Save" discount details
    $('.total-discount').toggleClass('hide-order-discount', !(data.totals.discountTotal && data.totals.discountTotal.value));
    $('.total-discount-text').text((data.totals.discountTotal && data.totals.discountTotal.formatted) || '');

    if (data.totals.discounts) {
        const $discountDetailsContainer = $('#discountDetails');
        const discountDetailMarkup = (label, value) => `
            <div class="d-flex flex-wrap justify-content-between">
                <div class="label">${label}</div>
                <div class="value">${value}</div>
            </div>
        `;

        $discountDetailsContainer.empty();
        data.totals.discounts.forEach(discount => {
            if (discount.applied && discount.relationship) {
                discount.relationship.forEach(discountRelationship => {
                    $discountDetailsContainer.append(discountDetailMarkup(
                        discountRelationship.name || discountRelationship.promotionID,
                        discountRelationship.formattedPrice
                    ));
                });
            } else if (!('applied' in discount)) {
                $discountDetailsContainer.append(discountDetailMarkup(discount.lineItemText, discount.price));
            }
        });
    }

    // Loyalty discount
    const $loyaltyDiscountRow = $summaryContainer.find('.loyalty-discount');
    $loyaltyDiscountRow.toggleClass('hide-order-discount', !(data.totals.showLoyaltyTotal && data.totals.loyalty && data.totals.loyalty.value > 0));
    $loyaltyDiscountRow.find('.loyalty-discount-total').text(`- ${(data.totals.loyalty && data.totals.loyalty.formatted) || ''}`);

    // Update shipping methods list
    updateShippingMethodsSelect(data.shipments[0].shippingMethods);

    // update Klarna Placements
    const klarnaPriceValue = data.totals.grandTotal.replace(/\D/g, ''); // $1,234.56 -> 123456 (Klarna wants micro amount)
    $('klarna-placement').attr('data-purchase-amount', klarnaPriceValue); // CAN NOT USE .data() HERE. Gotta update the attribute directly.
    if (window.Klarna && window.Klarna.OnsiteMessaging) {
        window.Klarna.OnsiteMessaging.refresh();
    }

    // Update PayPal option amount.
    if ($('.js_braintree_paypal_cart_button').length) {
        $('body').trigger('braintree:updateCartTotals');
    }
}

/**
 * Render approaching discount template
 * @param {Object} discount - cart model approachingDiscount object
 * @returns {string} alertContent - HTML string for the approaching discount alert content.
 *  Should match the approachingDiscounts.isml template
 */
function createApproachingDiscountContent(discount) {
    const showDetails = discount.discountDetails && discount.discountDetails !== 'null';
    let progressPct;
    if (discount.discountEarned) {
        progressPct = 100;
    } else {
        progressPct = discount.conditionThreshold ? (discount.conditionThreshold - discount.distanceFromThreshold) / discount.conditionThreshold : 0;
    }

    const alertContent = `<div class="d-flex align-items-center js-discount-alert" data-alert-id="${discount.ID}">
        <div class="progress" data-progress="${progressPct}">
            <svg> <use href="${'#'}gift"></use> </svg>
        </div>
        <div class="ml-2">
            <span class="approaching-discount-message">${discount.discountMsg}</span>
            <button class="approaching-discount-description ml-1 btn-link  ${showDetails ? '' : 'hidden'}" data-copy="${showDetails ? discount.discountDetails : ''}">
                ${window.resources && window.resources.cart.approachingDiscountsText}
            </button>
        </div>
    </div>`;

    return alertContent;
}
/**
 * Re-renders the approaching discount messages. First remove any existing alerts
 * Next loop through the discounts that were returned and display the alert.
 * Q3 2024 update: display approaching discount messages - max set by site preference maxDiscountAlerts
 * @param {Object} approachingDiscounts - updated approaching discounts and earned bonus product discounts for the cart
 */
function updateApproachingDiscounts(approachingDiscounts) {
    const $approachingDiscountsBox = $('.approaching-discount');
    const maxDiscountAlerts = (window.resources && window.resources.cart && window.resources.cart.maxDiscountAlerts) || 3;

    $('.approaching-discount .alert').remove();

    if (approachingDiscounts.length) {
        const limit = approachingDiscounts.length <= maxDiscountAlerts ? approachingDiscounts.length : maxDiscountAlerts;

        for (let i = 0; i < limit; i++) {
            const discount = approachingDiscounts[i];
            const alertContent = createApproachingDiscountContent(discount);

            $approachingDiscountsBox.alert({
                content: alertContent,
                theme: 'info',
                icon: '',
                placement: 'inline',
                containerId: 'approaching-discount',
                dismissible: false,
                autoClose: false
            });
            $(`.approaching-discount .alert .js-discount-alert[data-alert-id="${discount.ID}"]`).find('.progress').progressArc();
        }

        $approachingDiscountsBox.slideDown();
    } else {
        $approachingDiscountsBox.slideUp();
    }
}

/**
 * Re-order products, moving "error" products into cart-products__invalid
 * @param {Object} items - AJAX response from the server
 */
function updateProductOrder(items) {
    let $productElement;
    let hideInvalidProducts = true;
    let hideValidProducts = true;
    const $validProducts = $('.cart-products__valid');
    const $invalidProducts = $('.cart-products__invalid');

    items.forEach(lineItem => {
        $productElement = $(`.product-card[data-uuid=${lineItem.UUID}]`);

        if ($productElement.length) {
            // Re-order products, moving "error" products into cart-products__invalid
            if (!lineItem.available || !lineItem.isOrderable) {
                hideInvalidProducts = false;
                if ($productElement.parent('.cart-products__valid').length) {
                    $invalidProducts.prepend($productElement);
                }
            } else {
                hideValidProducts = false;
                if ($productElement.parent('.cart-products__invalid').length) {
                    $validProducts.prepend($productElement);
                }
            }
        }
    });

    $invalidProducts.toggleClass('d-none', hideInvalidProducts);
    $validProducts.toggleClass('d-none', hideValidProducts);
}

/**
 * Restores the digitalData object.
 * @param {Object} data The data to set in the digitalData object
 */
function restoreDTM(data) {
    if (!!data && 'digitalData' in window) {
        const propsToUpdate = ['pageInstanceID', 'page', 'user'];
        propsToUpdate.forEach((prop) => {
            if (data[prop]) {
                window.digitalData[prop] = data[prop];
            }
        });
        delete window.preQuickViewModalDigitalData;
    }
}

/**
 * Checks whether the basket is valid. if invalid displays error message and disables checkout button
 * @param {Object} data - AJAX response from the server
 */
function validateBasket(data) {
    $('.cart-error').empty();

    if (data.valid.error) {
        if (data.valid.message) {
            createErrorNotification(data.valid.message);
        } else {
            $('.grid__items').html(`<h1>${data.resources.emptyCartMsg}</h1>`);
            $('.js-number-items').text(data.resources.numberOfItems);
            $(document).trigger('minicart:updatecount', data.numItems);
            $(document).trigger('minicart:hide');
        }
    }

    $('.checkout-btn, .btn-checkout').toggleClass('disabled', data.valid.error);
    $('.apple-pay-container').toggleClass('d-none', data.valid.error);
}

/**
 * Handles changes to cart bonus products. Cart attribute initialBonusProducts
 * represents an array of bonus product UUIDs that were present at the start of
 * the operation.
 *  - If bonus products were added, refresh the page
 *  - If bonus products were removed, remove the card
 * @param {Ojbect} data - AJAX response from server
 */
function handleCartBonusProductChanges(data) {
    const initialBonusProducts = data.initialBonusProducts || [];
    const currentBonusProducts = data.items.filter(item => item.isBonusProductLineItem);

    if (!initialBonusProducts.length && !currentBonusProducts.length) {
        // no bonus products
        return;
    }

    const addedBonusProducts = currentBonusProducts.filter(currentItem => !initialBonusProducts.find(initialItem => currentItem.UUID === initialItem.UUID));
    const removedBonusProducts = initialBonusProducts
        .filter(initialItem => !data.items.find(currentItem => currentItem.UUID === initialItem.UUID))
        .map(obj => obj.UUID);

    if (addedBonusProducts.length) {
        $('body').trigger('cart:refresh');
    } else if (removedBonusProducts.length) {
        removeDeletedProductCards(removedBonusProducts);

        // Bonus product can be removed when cart doesn't qualify or when explicitly opted out
        if (data.bonusProductOptOut) {
            $('.bonus-product-restore').removeClass('d-none').show();
        }
    }
}

/**
 * Update selected delivery method and reflects in cart
 * @param {Object} $shippingMethodSelect - jQuery target of event
 */
function updateDeliveryMethod($shippingMethodSelect) {
    const url = $shippingMethodSelect.data('action-url');
    const urlParams = {
        methodID: $shippingMethodSelect.find(':selected').attr('data-shipping-id')
    };

    $.spinner().start();
    $('body').trigger('cart:beforeShippingMethodSelected');
    $.ajax({
        url: url,
        type: 'post',
        dataType: 'json',
        data: urlParams
    }).done(function (data) {
        if (data.error) {
            window.location.href = data.redirectUrl;
        } else {
            $('body').trigger('cart:update', data);
        }
        $('body').trigger('cart:shippingMethodSelected', data); // relic of app_storefront_base. Not handled.

        if (data.dtmEvent) {
            $('body').trigger('cart:emitEvents', { events: data.dtmEvent });
        }

        $.spinner().stop();
    }).fail(function (err) {
        if (err.redirectUrl) {
            window.location.href = err.redirectUrl;
        } else {
            createErrorNotification(err.responseJSON.errorMessage);
            $.spinner().stop();
        }
    });
}

/**
 * Updates cart and total when quantiy changed
 * @param {Object} $thisQtyField - this from quantity change event
 */
function quantityUpdate($thisQtyField) {
    let quantity = parseInt($thisQtyField.val(), 10);
    const preSelectQty = parseInt($thisQtyField.data('pre-select-qty'), 10);
    if (isNaN(quantity)) { // invalid number / blank space
        quantity = preSelectQty;
        $thisQtyField.val(quantity);
    }
    const $productCard = $thisQtyField.closest('.product-card');

    if (quantity < 1) {
        const $removeBtn = $productCard.find('.js-remove-product');
        $removeBtn.trigger('click');
        return;
    }

    // else
    const maxValue = parseInt($thisQtyField.attr('max'), 10);
    if (quantity > maxValue) {
        quantity = maxValue;
        // Fixes edge case where we are at max currently and the below update won't process
        $thisQtyField.val(quantity);
    }

    const uuid = $productCard.data('uuid');

    if (preSelectQty !== quantity) {
        const urlParams = {
            pid: $productCard.data('pid'),
            quantity: quantity,
            prevQuantity: preSelectQty,
            uuid: uuid
        };
        const url = utils.setUrlData($thisQtyField.data('action'), urlParams);

        $.spinner().start();

        const reservationTimerHelper = require('../components/reservationTimer');
        $.ajax({
            url: url,
            type: 'get',
            context: $thisQtyField,
            dataType: 'json'
        }).done(data => {
            if (data.error) {
                $('body').trigger('cart:update', data);
            } else {
                const lineItem = data.items.find(item => item.UUID === uuid);
                const newQuantity = lineItem.quantity;

                $thisQtyField.val(newQuantity).data('pre-select-qty', newQuantity);
                $thisQtyField.closest('.input-group').find('.js-quantity-add')
                    .prop('disabled', (newQuantity >= lineItem.quantityOptions.maxOrderQuantity)); // toggle disabled state of + button

                $('body').trigger('cart:update', data);

                $.alert({
                    content: window.resources.cartUpdated,
                    placement: 'banner',
                    theme: 'info'
                });

                if ($productCard.hasClass('bonus-product-line-item')) {
                    location.reload();
                }

                reservationTimerHelper.updateReservationData(data);

                if (data.dtmEvent) {
                    $('body').trigger('cart:emitEvents', { events: data.dtmEvent });
                }

                if ('dtmLayer' in data) {
                    basketHelper.updateDTM(data.dtmLayer, ['page', 'user', 'cart']);
                }
            }
        }).fail(err => {
            $thisQtyField.val(parseInt(preSelectQty, 10));
            if (err.responseJSON.redirectUrl) {
                window.location.href = err.responseJSON.redirectUrl;
            } else if (!err.responseJSON.reservation) {
                createErrorNotification(err.responseJSON.errorMessage);
                $thisQtyField.val(parseInt(preSelectQty, 10));
            } else {
                // Show a banner
                reservationTimerHelper.updateReservationData(err.responseJSON);
            }
        }).always(() => {
            $.spinner().stop();
        });
    }
    return;
}

/**
 * Triggers alert when promo successfully added
 * @param {string} successMsg - text for successful promo code add message
 */
function promoCodeSuccess(successMsg) {
    $('.promo-code-form').hide();
    $.alert({
        content: successMsg,
        placement: 'banner',
        theme: 'success'
    });
}

/**
 * Sumbmits promo code form and applies to cart if successful
 * @returns {boolean} false;
 */
function addPromoCode() {
    const $form = $('.promo-code-form');
    const $couponCodeField = $form.find('#couponCode');
    const $errorMessageDiv = $form.find('.promo-code-input .invalid-feedback');
    const promoCodeToBeAdded = $couponCodeField.val().trim();

    if (!promoCodeToBeAdded) {
        $couponCodeField.addClass('is-invalid');
        $errorMessageDiv.html($couponCodeField.data('missing-error'));
        return false;
    }

    $.spinner().start();
    $couponCodeField.removeClass('is-invalid');
    $errorMessageDiv.empty();

    $('body').trigger('promotion:beforeUpdate');

    $.ajax({
        url: $form.attr('action'),
        type: 'GET',
        dataType: 'json',
        data: $form.serialize()
    }).done(data => {
        if (data.error) {
            $couponCodeField.addClass('is-invalid');
            $errorMessageDiv.html(data.errorMessage);
        } else if (data.csrferror) {
            cartutils.toggleErrorOnCSRF(true, data);
        } else {
            $('body').trigger('cart:update', data);
            $('body').trigger('promotion:success', data);  // relic of app_storefront_base. Not handled.

            // if user is trying to add a fans/followers code or partner code, this will show a modal instructing them to go to the entry page
            cartutils.toggleGoToContent(true, data);

            // if promo code was applied, show message
            if (promoCode.isPromoCodeApplied(data.totals, promoCodeToBeAdded)) {
                promoCodeSuccess(data.totals.discountSuccessText);
            }

            if (data.isFullyCoveredByPromoCode) {
                $('.js_braintree_paypal_cart_button').addClass('disabled');
            }

            if (data.dtmEvent) {
                $('body').trigger('cart:emitEvents', { events: data.dtmEvent });
            }
        }

        $couponCodeField.val('').trigger('change');
        $couponCodeField.closest('.form-group').removeClass('focus-fill');
        $.spinner().stop();

        if ('dtmLayer' in data) {
            basketHelper.updateDTM(data.dtmLayer);
        }
    }).fail(err => {
        if (err.responseJSON.redirectUrl) {
            window.location.href = err.responseJSON.redirectUrl;
        } else {
            createErrorNotification(err.errorMessage);
            $.spinner().stop();
        }
    });
    return false;
}

/**
 * Reset the form and open/close share my cart form.
 * @param {Object} $cartShareModal - cart share form bootstrap modal
 */
function resetShareCartForm($cartShareModal) {
    // reset the form
    const $shareForm = $('#cart-share').find('form');
    $shareForm[0].reset();
    $shareForm.find('.focus-fill').removeClass('focus-fill');
    $shareForm.find('.is-invalid').removeClass('is-invalid');
    // reset the characters remaining text
    $('#share-message').trigger('keyup');

    // hide the modal
    $cartShareModal.modal('hide');
}

/**
 * Updates character counter as user types message for cart share modal
 * @param {Object} $this - jQuery target of event
 * @returns {boolean} isValid - returns true as long as message is shorter than max length
 */
function shareCartCharacterCount($this) {
    const maxLength = $this.attr('maxlength');
    const len = $this.val().length;
    const isValid = len <= maxLength;
    if (isValid) {
        $('#share-cart-char-remaining').text(maxLength - len);
    }
    return isValid;
}

/**
 * This event check the email mandatory and Ajax service call to send the
 * email templates from EcommHub for share my cart.
 * and show the alert message.
 */
function shareCart() {
    const $cartShareModal = $('#cart-share');
    const $form = $('.cart-share-form');
    const $emailInvalidFeedback = $form.find('input[type=email]~.invalid-feedback');
    const $emailHelpText = $form.find('input[type=email]~.form-help-text');

    $('.modal-content').spinner().start();

    $emailInvalidFeedback.html('').hide();
    $emailHelpText.show();

    if ($form.validateForm()) {
        $.ajax({
            url: $form.attr('action'),
            type: 'post',
            dataType: 'json',
            data: $form.serialize()
        }).done((data) => {
            if (data.success === true) {
                resetShareCartForm($cartShareModal);
                $.alert({
                    content: data.message,
                    placement: 'banner',
                    theme: 'success'
                });
            } else if (data.isValidEmail === false) {
                $emailInvalidFeedback.html(data.message).show();
                $emailHelpText.hide();
            } else {
                $.alert({
                    content: data.message,
                    placement: 'banner',
                    theme: 'danger',
                    parentSelector: '.modal.show .modal-content',
                    containerAttachment: 'prepend'
                });
            }
        }).fail((err) => {
            $.alert({
                content: err.responseJSON.message,
                placement: 'banner',
                theme: 'danger',
                parentSelector: '.modal.show .modal-content',
                containerAttachment: 'prepend'
            });
            resetShareCartForm($cartShareModal);
        });
    }
    $.spinner().stop();
}

/**
 * On scroll function to check if standard checkout button offscreen
 * to show the sticky checkout button
 */
function initStickyCheckoutButton() {
    if (breakpoints.isHighRes()) {
        return;
    }

    const $stickyCheckout = $('.checkout-continue__mobile');
    const $standardCheckout = $('.checkout-continue__desktop');

    const displayStickyCheckout = () => {
        if ($standardCheckout.isInViewport(true, true)) {
            $stickyCheckout.slideUp();
        } else {
            $stickyCheckout.slideDown();
        }
    };

    // add padding to footer to prevent sticky container from overlapping footer content.
    $('footer').css('padding-bottom', $stickyCheckout.outerHeight());

    if (window.resources && window.resources.initStickyCheckoutOnLoad) {
        displayStickyCheckout();
    }

    // on scroll, if the static checkout buttons are out of the viewport, show the sticky buttons. Else hide them.
    window.addEventListener('scroll',
        throttle(displayStickyCheckout, 500, true),
        { passive: true }
    );
}

/**
 * Asynchronously loads the product cards. Used on page load and every time the Cart or Save For Later list is updated.
 */
function asyncLoadProductCards() {
    const $cartContainer = $('.cart-product-line-items-container');
    const $sflContainer = $('.sfl-product-line-items-container');

    let requestUrl;
    if (location.search.includes('savedPageSize')) {
        const pageUrl = new URL(location.href);
        const newUrl = new URL(window.resources.asyncProductLineItemsURL, pageUrl.origin);
        newUrl.searchParams.set('savedPageSize', pageUrl.searchParams.get('savedPageSize'));
        requestUrl = newUrl.toString();
    } else {
        requestUrl = window.resources.asyncProductLineItemsURL;
    }

    $.get(requestUrl).done((htmlString) => {
        $.spinner().stop();
        const $markup = $(htmlString);
        const $cart = $markup.find('.js-cart-items');
        $cartContainer.html($cart.html());

        if ($sflContainer.length) {
            const $sfl = $markup.find('.js-sfl-items');
            $sflContainer.html($sfl.html());
            if ($sflContainer.find('.product-info').length) {
                $('.js-save-for-later-container').slideDown();
            }
        }

        // Add availability message when cart qty has been automatically changed to available amt
        const $cardContainer = $('.cart-product-line-items-container');
        const $productsList = $cardContainer.data('auto-updated-uuids');
        if ($productsList) {
            const productsToUpdate = $productsList.split(',');
            productsToUpdate.forEach(uuid => {
                const $productCard = $(`.uuid-${uuid}`);
                const newQuantity = $productCard.find('.js-quantity').val();
                const syntheticLineItem = {
                    availability: {
                        messages: [{
                            content: window.resources.quantityUpdated.replace('{0}', newQuantity),
                            theme: 'danger',
                            dismissible: true
                        }]
                    }
                };
                updateAvailability(syntheticLineItem, $productCard);
            });
            $cardContainer.data('auto-updated-uuids', '');
        }
    });
}

/**
 * Allows user to edit the bonus product in their cart
 * @param {Object} $this - jQuery target of event
 */
function editBonusProduct($this) {
    /**
     * Stolen from product/base.js so we don't need to import and execute the whole file.
     * Parses the html for a modal window
     * @param {string} html - representing the body and footer of the modal window
     * @return {Object} - Object with properties body and footer.
     */
    const parseHtml = function (html) {
        const $html = $('<div>').append($.parseHTML(html));
        const body = $html.find('.choice-of-bonus-product');
        const footer = $html.find('.modal-footer').children();

        return { body, footer };
    };

    $.spinner().start();
    $.ajax({
        url: $this.data('url'),
        method: 'GET',
        dataType: 'json'
    }).done(data => {
        // This handler was stolen from product/base.js so we don't need to import and execute the whole file.
        $('#chooseBonusProductModal').remove();

        const bonusUrl = data.bonusChoiceRuleBased ? data.showProductsUrlRuleBased : data.showProductsUrlListBased;
        const htmlString = utils.createModalMarkup('', {
            modalId: 'chooseBonusProductModal',
            title: data.labels.selectprods,
            customClasses: 'choose-bonus-product-dialog'
        });

        $('body').append(htmlString);

        $('#chooseBonusProductModal .modal-dialog').data({
            'total-qty': data.maxBonusItems,
            UUID: data.uuid,
            pliUUID: data.pliUUID,
            addToCartUrl: data.addToCartUrl,
            pageStart: 0,
            pageSize: data.pageSize,
            moreURL: data.showProductsUrlRuleBased,
            bonusChoiceRuleBased: data.bonusChoiceRuleBased
        });

        $.ajax({
            url: bonusUrl,
            method: 'GET',
            dataType: 'html'
        }).done(html => {
            const parsedHtml = parseHtml(html);
            $('#chooseBonusProductModal .modal-body').html(parsedHtml.body);
            $('#chooseBonusProductModal .modal-footer').html(parsedHtml.footer);
            $('#chooseBonusProductModal').modal('show');
        }).always(() => {
            $.spinner().stop();
        });
    }).fail(() => {
        $.spinner().stop();
    });
}


/**
 * Fires the call that restores a bonus product
 * @param {Object} btn Clicked button
 */
function restoreBonusProduct(btn) {
    const url = $(btn).data('action');
    $.ajax({
        url: url,
        type: 'get',
        dataType: 'json'
    }).done(function (data) {
        handleCartBonusProductChanges(data.basket);
        updateApproachingDiscounts(data.basket.approachingDiscounts);

        if (data.dtmEvent) {
            $('body').trigger('cart:emitEvents', { events: data.dtmEvent });
        }
    });
}

/**
 * Triggerd edit gift card quantity and amount from cart
 * @param {Object} $this - jQuery target of event
 */
function editCartProduct($this) {
    const editProductUrl = $this.attr('href');
    const isGiftCard = $this.data('isgiftcard');
    if (isGiftCard) {
        window.location = editProductUrl;
        return;
    }
    baseQuickViewJS.launchEditProduct(editProductUrl);
}

/**
 * Updates global cart editability
 * @param {Object} response - event response
 */
function updateGlobalAddToCart(response) {
    const $dialog = response.$productContainer;
    const $globalAvailability = $dialog.find('.global-availability');

    $dialog.find('.update-cart-product-global').attr('disabled',
        !$globalAvailability.data('ready-to-order') ||
        !$globalAvailability.data('available')
    );
}

/**
 * Updates details of a product line item
 * @param {Object} lineItem - AJAX response from the server
 * @param {string} newProductId - The product id of the product line item to update
 * @param {string?} sizeId - The ID of the selected size option. Valued if called from the size dropdown change event.
 */
function updateProductDetails(lineItem, newProductId, sizeId) {
    const $productCard = $(`.product-card[data-uuid=${lineItem.UUID}]`);
    $productCard.data('pid', newProductId);

    $productCard.find('.product-image').attr({
        src: lineItem.images.small[0].url,
        alt: lineItem.images.small[0].alt,
        title: lineItem.images.small[0].title
    });

    if (lineItem.variationAttributes) {
        const colorAttr = lineItem.variationAttributes.find(attr => attr.attributeId === 'color');
        const sizeAttr = lineItem.variationAttributes.find(attr => attr.attributeId === 'size');
        const dimensionAttr = lineItem.variationAttributes.find(attr => attr.attributeId === 'dimension');

        if (colorAttr) {
            $(`.${colorAttr.displayName}-${lineItem.UUID} .value`).text(colorAttr.displayValue);
        }

        if (sizeAttr) {
            const $sizeElement = $(`.${sizeAttr.displayName}-${lineItem.UUID} .value`);

            if ($sizeElement.hasClass('js-cart-size-selector')) {
                // update the select box
                $sizeElement.empty();

                if (lineItem.additionalSizeOptions && lineItem.additionalSizeOptions.length) {
                    lineItem.additionalSizeOptions.forEach(sizeOption => {
                        $sizeElement.append(`<option value="${sizeOption.pid}" ${sizeOption.selected ? 'selected' : ''}>${sizeOption.size}</option>`);
                    });
                    $sizeElement.data('sizeid', sizeId);
                } else {
                    $sizeElement.append(`<option value="${lineItem.id}">${sizeAttr.displayValue}</option>`);
                }

                const $sku = $sizeElement.parent().siblings('.js-sku-value');
                if ($sku.length > 0) {
                    $sku.text(sizeId);
                }
            } else {
                // this is a span element
                $sizeElement.text(sizeAttr.displayValue);
            }
        }

        if (dimensionAttr) {
            $(`.${dimensionAttr.displayName}-${lineItem.UUID} .value`).text(dimensionAttr.displayValue);
        }
    }

    $productCard.find('.js-remove-product').data('pid', newProductId);
    $productCard.find('.js-saveForLater-product').data('pid', newProductId);
    $productCard.find('.price .sales .value').text(lineItem.price.sales.formatted).attr('content', lineItem.price.sales.decimalPrice);

    if (lineItem.price.list) {
        $productCard.find('.price .list .value').text(lineItem.price.list.formatted).attr('content', lineItem.price.list.decimalPrice);
    }
}

/**
 * Handles the response when editing a line item (from quickview or from changing the size attribute)
 * @param {Object} data - AJAX response from the server
 * @param {string} uuid - The uuid of the product line item to update
 * @param {string?} sizeId - The ID of the selected size option. Valued if called from the size dropdown change event.
 */
function editLineItemSuccessHandler(data, uuid, sizeId) {
    const reservationTimerHelper = require('../components/reservationTimer');
    const lineItem = data.cartModel.items.find(item => item.UUID === uuid);

    baseQuickViewJS.hideQuickview();

    $('body').trigger('cart:update', data.cartModel);
    updateProductDetails(lineItem, data.newProductId, sizeId); // updates image, attributes, prices

    if (data.uuidToBeDeleted) {
        $(`.product-card[data-uuid=${data.uuidToBeDeleted}`).remove();
    }

    window.dispatchEvent(new Event('cart:updated:dtm'));

    if (!data.reservation) {
        // if you no longer have a reservable product...
        if (window.resources.reservation && !data.numberOfReservableProducts) {
            // ... but you did before, remove the reservation.
            reservationTimerHelper.getDestroyTimer()();
            delete window.resources.reservation;
        }
        // display the standard "Your cart has been updated" banner.
        $.alert({
            content: window.resources.cartUpdated,
            placement: 'banner',
            theme: 'info'
        });
    } else {
        // you now have (or still have) a reservable product. Update the data.
        if (!data.reservation.messages) {
            data.reservation.messages = {}; // eslint-disable-line no-param-reassign
            $.alert({
                content: window.resources.cartUpdated,
                placement: 'banner',
                theme: 'info'
            });
        }
        reservationTimerHelper.updateReservationData(data);
    }
    if (window.preQuickViewModalDigitalData) {
        // Restoring the digital data back to that of cart page before opening the quick view modal.
        window.preQuickViewModalDigitalData = JSON.parse(window.preQuickViewModalDigitalData);
        setTimeout(function () {
            restoreDTM(window.preQuickViewModalDigitalData);
        }, 1000);
    } else if ('dtmLayer' in data) {
        basketHelper.updateDTM(data.dtmLayer, ['pageInstance', 'page', 'user']);
    }

    if (data.dtmEvent) {
        $('body').trigger('cart:emitEvents', { events: data.dtmEvent });
    }

    basketHelper.updateDTM(data.dtmLayer, ['cart']);

    // Update Stylitics widget if it exists
    $('body').trigger('stylitics:update');
}

/**
 * Event handler for when the product size select element is changed.
 * @param {Object} $this - jQuery target of event
 */
function updateItemSize($this) {
    const newProductId = $this.val();

    if (!newProductId) {
        return;
    }

    const originalSize = $this.data('sizeid');
    const uuid = $this.data('uuid');
    const targetUrl = $this.data('size-update-url');
    const selectedQuantity = $(`#quantity-${uuid}`).val();
    const targetData = {
        uuid: uuid,
        pid: newProductId,
        quantity: parseInt(selectedQuantity, 10)
    };

    $.spinner().start();

    $.ajax({
        url: targetUrl,
        type: 'post',
        data: targetData,
        dataType: 'json'
    }).done(function (data) {
        if (data.error) {
            $.alert({
                content: data.errorMessage,
                theme: 'danger',
                placement: 'banner',
                dismissible: true
            });
            // Reset dropdown to original value
            $this.find(`option[value=${originalSize}]`).prop('selected', true);
        } else {
            editLineItemSuccessHandler(data, uuid, newProductId);
        }
    }).fail(function (error) {
        $.alert({
            content: error.responseJSON.errorMessage || 'An unknown error occurred',
            theme: 'danger',
            placement: 'banner',
            dismissible: true
        });
        // Reset dropdown to original value
        $this.find(`option[value=${originalSize}]`).prop('selected', true);
    }).always(function () {
        $.spinner().stop();
    });
}

/**
 * Updates product attributes in quick view
 * @param {Object} source - dom element of event (the Add to Cart button)
 */
function updateQuickViewProduct(source) {
    const $updateCartUrl = $(source).closest('.cart-and-ipay').find('.update-cart-url');
    const uuid = $updateCartUrl.data('uuid');

    const form = {
        uuid: uuid,
        pid: basketHelper.getPidFromQuickview($(source)),
        quantity: $updateCartUrl.data('selected-quantity')
    };

    baseQuickViewJS.startSpinner();
    $('body').trigger('cart:beforeUpdate');

    $.ajax({
        url: $updateCartUrl.val(),
        type: 'post',
        context: source,
        data: form,
        dataType: 'json'
    }).done(data => {
        $.spinner().stop();
        editLineItemSuccessHandler(data, uuid);
    }).fail(err => {
        if (err.responseJSON.redirectUrl) {
            window.location.href = err.responseJSON.redirectUrl;
        } else if (err.responseJSON.reservation && typeof resources !== 'undefined') {
            const reservationTimerHelper = require('../components/reservationTimer');
            $.spinner().stop();
            baseQuickViewJS.hideQuickview();
            // show the error banner
            reservationTimerHelper.updateReservationData(err.responseJSON);
        } else {
            $.spinner().stop();
            createErrorNotification(err.responseJSON.errorMessage);
        }
    });
}

/**
 * Click handler for when user clicks the add to cart button on a product in the save for later section
 * @param {Object} $this - jQuery target of event (the clicked button)
 */
function moveToCart($this) {
    let pid = $this.data('pid').toString();
    let url = $('.js-save-for-later-container').data('movetocart-url');
    let formData = {
        pid: pid,
        isSaveForLater: true,
        quantity: 1
    };

    $.spinner().start();
    $('body').trigger('cart:beforeUpdate');
    $.ajax({
        url: url, // Cart-AddProduct
        type: 'POST',
        data: formData,
        dataType: 'json'
    }).done(function (data) {
        if (data.error) {
            $.spinner().stop();
            createErrorNotification(data.message);
            return;
        }

        // Saves uuids that were updated in cart middleware
        if (data.autoUpdatedItems && data.autoUpdatedItems.length > 0) {
            const itemList = data.autoUpdatedItems.join(',');
            const $cardContainer = $('.cart-product-line-items-container');
            $cardContainer.data('auto-updated-uuids', itemList);
        }

        if ($('.js-cart-empty').length) {
            // cart was previously empty. reload page to get the real cart page.
            if (data.dtmEvent) {
                window.localStorage.setItem('dtmEvent', JSON.stringify(data.dtmEvent));
            }
            window.location.reload();
            return;
        }

        $.spinner().stop();

        asyncLoadProductCards();

        $.alert({
            content: data.alertMsg,
            theme: 'success',
            placement: 'banner'
        });

        $('.js-saveforlater-listsize').html(data.listSizeMsg).data('listsize', data.lineItemsTotalCount);
        if (!data.lineItemsTotalCount) {
            // list is empty
            $('.js-save-for-later-container').slideUp();
        }

        // update stuff
        updateCartTotals(data.cart);
        validateBasket(data.cart);
        updateApproachingDiscounts(data.cart.approachingDiscounts);
        basketHelper.updateRewardsCardAndBanner(data.cart);

        if (data.dtmEvent) {
            $('body').trigger('cart:emitEvents', { events: data.dtmEvent });
        }

        if ('dtmLayer' in data) {
            basketHelper.updateDTM(data.dtmLayer, ['pageInstance', 'page', 'user', 'cart']);
        }

        // Update Stylitics widget if present
        $('body').trigger('stylitics:update');
    }).fail(function (err) {
        if (err.responseJSON.redirectUrl) {
            location.href = err.responseJSON.redirectUrl;
        } else {
            createErrorNotification(err.responseJSON.errorMessage);
            $.spinner().stop();
        }
    });
}

if (window.resources && window.resources.idMe && window.resources.idMe.showIdMeSuccess) {
    $.alert({
        content: window.resources.idMe.idMeSuccessMessage,
        theme: 'success'
    });

    delete window.resources.idMe.showIdMeSuccess;
}

if (window.dw &&
    window.dw.applepay &&
    window.ApplePaySession &&
    window.ApplePaySession.canMakePayments()) {
    $('body').addClass('apple-pay-enabled');
}

module.exports = {
    init: function () {
        asyncLoadProductCards();

        $('body')
            .on('click', '.js-oos-remove', function (e) {
                e.preventDefault();
                $(this).closest('.product-card').find('.js-remove-product').trigger('click');
            })
            .on('click', '.js-quantity-add', function () {
                const $qtyField = $(this).closest('.input-group').find('.js-quantity');
                let currentVal = parseInt($qtyField.val(), 10);
                if (currentVal < parseInt($qtyField.attr('max'), 10)) {
                    $qtyField.val(++currentVal).trigger('change');
                }
            })
            .on('click', '.js-quantity-remove', function () {
                const $qtyField = $(this).closest('.input-group').find('.js-quantity');
                let currentVal = parseInt($qtyField.val(), 10);
                if (currentVal > 1) {
                    $qtyField.val(--currentVal).trigger('change');
                } else {
                    $qtyField.closest('.product-card').find('.js-remove-product').trigger('click');
                }
            })
            .on('change', '.js-quantity', function () {
                clearTimeout(quantityChangeDebounce);
                const $qtyField = $(this);
                quantityChangeDebounce = setTimeout(function () {
                    quantityUpdate($qtyField);
                }, 500);
            })
            .on('click', '.cart-page .bonus-product-button', function () {
                editBonusProduct($(this));
            })
            .on('click', '.cart-page .js-restore-bonus-product', function () {
                $('.bonus-product-restore').hide();
                restoreBonusProduct(this);
            })
            .on('click', '.cart-page .js-edit-product', function (e) {
                e.preventDefault();
                editCartProduct($(this));
            })
            .on('click', '.cart-page .js-oos-edit', function (e) {
                e.preventDefault();
                $(this).closest('.product-card').find('.js-edit-product').trigger('click');
            })
            .on('click', '.js-saveForLater-add-to-cart', function (e) {
                e.preventDefault();
                moveToCart($(this));
            })
            .on('product:updateAddToCart', function (e, response) {
                updateGlobalAddToCart(response);
            })
            .on('click', '.update-cart-product-global', function (e) {
                e.preventDefault();
                updateQuickViewProduct(this);
            })
            .on('click', '.js-idme-drawer', function () {
                const $this = $(this);

                if (!$idMeDrawer) {
                    $idMeDrawer = $.drawer({
                        title: $this.data('drawer-title'),
                        src: $this.data('drawer-src'),
                        size: 'sm',
                        destroyOnClose: false,
                        returnFocusTo: '.js-idme-drawer'
                    });
                } else {
                    $idMeDrawer.open();
                }
            })
            .on('click', '.js-remove-id-me', function () {
                const $this = $(this);
                const url = $this.data('url');
                if (url) {
                    $.ajax({
                        url: url,
                        type: 'post',
                        success: function () {
                            window.location.reload();
                        }
                    });
                }
            })
            .on('change', '.shippingMethods', function () {
                updateDeliveryMethod($(this));
            })
            .on('submit', '.promo-code-form', function (e) {
                e.preventDefault();
                addPromoCode();
            })
            .on('click', '.share-my-cart__link', function (e) {
                e.preventDefault();
                resetShareCartForm();
            })
            .on('click', '.js-cart-modal', function (e) {
                e.preventDefault();
                createModal($(this));
            })
            .on('click', '.js-close-share-cart-modal, #cart-share .close', function () {
                resetShareCartForm($(this));
            })
            .on('submit', '.cart-share-form', function (e) {
                e.preventDefault();
                shareCart();
            })
            .on('keyup', '#share-message', function () {
                shareCartCharacterCount($(this));
            })
            .on('change', '.js-cart-size-selector', function () {
                updateItemSize($(this));
            })
            .on('cart:update', function (e, basket) {
                // triggered from basketHelper.js when removing item from cart
                if (basket.totals && basket.totals.discountsHtml) {
                    $('.js-coupons-and-promos').html(basket.totals.discountsHtml);
                }
                updateCartTotals(basket);
                updateAllProductCards(basket.items); // this calls updateAvailability() and updateProductQuantity()

                if (Array.isArray(basket.autoUpdatedItems)) {
                    updateCartQuantityChangeMessage(basket.autoUpdatedItems);
                }

                if (Array.isArray(basket.autoDeletedItems)) {
                    removeDeletedProductCards(basket.autoDeletedItems);
                }

                updateApproachingDiscounts(basket.approachingDiscounts);
                updateProductOrder(basket.items); // moves invalid products into .cart-products__invalid
                basketHelper.updateRewardsCardAndBanner(basket);
                validateBasket(basket);
                $('body').trigger('setShippingMethodSelection', basket); // handled in cartInStorePickup.js
                handleCartBonusProductChanges(basket); // may trigger cart:refresh
            })
            .on('cart:refresh', function () {
                asyncLoadProductCards();
            });

        $('[data-toggle="tooltip"]').tooltip();
        $('.approaching-discount .progress').progressArc();

        initStickyCheckoutButton();
        basketHelper.processOnloadEvents();
    }
};
